export const length = (obj) =>  Object.keys(obj).length;
export const pack = (entries) => Object.fromEntries(entries);
export const toArray = Object.entries;
export const pick = (obj, ...keys) => pack(keys.filter(key => key in obj).map(key => [key, obj[key]]));
export const inclusivePick = (obj, ...keys) => pack(keys.map(key => [key, obj[key]]));
export const reject = (obj, ...keys) => pack(toArray(obj).filter(([key]) => !keys.includes(key)));
export const filter = (obj, filterFunction) => pack(toArray(obj).filter(filterFunction));
export const packedMap = (obj, mapFunction) => pack(toArray(obj).map(mapFunction));
export const find = (obj, findFunction) => pack([toArray(obj).find(findFunction)]);
export const map = (obj, mapFunction) => toArray(obj).map(mapFunction);

export const indexPick = (obj, targetIndex) => filter(obj, (_obj, index) => index == targetIndex);
export const toArrayWithKey = (obj, keyAs = "key") => toArray(obj).map(([key, value]) => ({ [keyAs]: key, ...value }));
export const mappedObj = (obj, valueKey) => pack(toArray(obj).map(([key, value]) => [key, value[valueKey]]));
export const leftMerge = (obj1, obj2) => pack(toArray(obj1).map(([key, value]) => [key, { ...value, ...obj2[key] }]));
export const firstElement = obj => indexPick(obj, 0);
export const lastElement = obj => indexPick(obj, toArray(obj).length - 1);
export const present = obj => !isEmpty(obj);
export const isSinglePair = obj => toArray(obj).length == 1;
export const key = obj => isSinglePair(obj) ? toArray(obj)[0][0] : null;
export const isEmpty = (obj) => {
  for (let i in obj) return false;
  return true;
};
export const presence = (obj, fallback = null) => {
  return present(obj) ? obj : fallback
}

export const isObject = (item) => {
  return (item && typeof item === 'object' && !Array.isArray(item));
};

export const deepMerge = (obj1, obj2) => {
  let output = Object.assign({}, obj1);
  if (isObject(obj1) && isObject(obj2)) {
    Object.keys(obj2).forEach(key => {
      if (isObject(obj2[key])) {
        if (!(key in obj1))
          Object.assign(output, { [key]: obj2[key] });
        else
          output[key] = deepMerge(obj1[key], obj2[key]);
      } else {
        Object.assign(output, { [key]: obj2[key] });
      }
    });
  }
  return output;
};

export const orderedDeepMerge = (...collection) => {
  return collection.reduce((mergerObj, obj) => deepMerge(mergerObj, obj), {});
};

export const deepConvertKeysFromCamelToSnakeCase = (obj) => {
  if (Array.isArray(obj)) {
    return obj.map(item => deepConvertKeysFromCamelToSnakeCase(item));
  } else if (isObject(obj)) {
    return Object.keys(obj).reduce((acc, key) => {
      const snakeKey = key.replace(/[A-Z]/g, letter => `_${letter.toLowerCase()}`);
      acc[snakeKey] = deepConvertKeysFromCamelToSnakeCase(obj[key]);
      return acc;
    }, {});
  }
  return obj;
}

export const deepStringifyValues = (obj) => {
  if (Array.isArray(obj)) {
      return obj.map(item => deepStringifyValues(item));
  } else if (obj !== null && typeof obj === 'object') {
    return Object.keys(obj).reduce((acc, key) => {
      const value = obj[key];
      acc[key] = deepStringifyValues(value);
      return acc;
    }, {});
  }
  // `obj == null` verifies whether `obj` is either `null` or `undefined` (since `==` in JavaScript allows type coercion).
  return obj == null ? null : String(obj);
}

export const objectToHtmlDataAttributesMap = (obj) => {
  const toKebabCase = (str) => {
    return str.replace(/([a-z])([A-Z])/g, "$1-$2").toLowerCase();
  }
  let modifiedData = {}

  for (let key of Object.keys(obj)){
    modifiedData[`data-${toKebabCase(key)}`] = obj[key];
  }
  return modifiedData;
}

export const some = (obj, checkFunction) => present(filter(obj, checkFunction));

export const mergeObjectsConditionally = (base = {}, conditionals = []) => {
  const conditionalArray = Array.isArray(conditionals) ? conditionals : [conditionals];
  let result = { ...base };

  for (const { condition, object } of conditionalArray) {
    if (condition && object) {
      result = { ...result, ...object };
    }
  }

  return result;
}